import React, { useMemo } from 'react';
import { getDayName, formatTemp, getWeatherIcon, filterForecastDays, DEGREE_SYMBOL } from '../../utils/weatherUtils';
import Icon from '../../utils/icons';
import './TVForecast.css';

function TVForecast({ data, compact = false }) {
  // Filter out past days (timezone-safe) and limit to 5
  const filteredDays = useMemo(() => filterForecastDays(data?.days, 5), [data?.days]);

  if (!filteredDays || filteredDays.length === 0) {
    return null;
  }

  if (compact) {
    return (
      <div className="tv-forecast compact">
        <div className="tv-forecast-header">
          <Icon name="calendar" size={18} />
          <h3>5-Day Forecast</h3>
        </div>
        <div className="tv-forecast-compact-list">
          {filteredDays.map((day, index) => (
            <div key={index} className={`tv-forecast-compact-day ${index === 0 ? 'today' : ''}`}>
              <span className="compact-day-name">
                {index === 0 ? 'Today' : getDayName(day.date)}
              </span>
              {day.condition?.icon && (
                <img
                  src={getWeatherIcon(day.condition.icon)}
                  alt=""
                  className="compact-icon"
                />
              )}
              <div className="compact-temps">
                <span className="compact-high">{Math.round(day.temperature_max)}{DEGREE_SYMBOL}</span>
                <span className="compact-low">{Math.round(day.temperature_min)}{DEGREE_SYMBOL}</span>
              </div>
              {day.precipitation_chance > 0 && (
                <span className="compact-precip">
                  <Icon name="droplet" size={12} />
                  {Math.round(day.precipitation_chance)}%
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="tv-forecast">
      <div className="tv-forecast-header">
        <Icon name="calendar" size={24} />
        <h3>5-Day Forecast</h3>
      </div>

      <div className="tv-forecast-list">
        {filteredDays.map((day, index) => {
          const isToday = index === 0;
          
          return (
            <div key={index} className={`tv-forecast-day ${isToday ? 'today' : ''}`}>
              <div className="tv-forecast-day-name">
                <span className="day-name">{getDayName(day.date)}</span>
                {isToday && <span className="today-badge">Today</span>}
              </div>
              
              <div className="tv-forecast-icon">
                {day.condition?.icon && (
                  <img
                    src={getWeatherIcon(day.condition.icon, '2x')}
                    alt={day.condition?.description}
                  />
                )}
                <span className="condition-text">{day.condition?.main}</span>
              </div>

              <div className="tv-forecast-precip">
                {day.precipitation_chance > 0 ? (
                  <>
                    <Icon name="droplet" size={18} />
                    <span>{Math.round(day.precipitation_chance)}%</span>
                  </>
                ) : (
                  <span className="no-precip">-</span>
                )}
              </div>

              <div className="tv-forecast-temps">
                <span className="temp-high">
                  {Math.round(day.temperature_max)}{DEGREE_SYMBOL}
                </span>
                <div className="temp-bar">
                  <div 
                    className="temp-bar-fill"
                    style={{
                      width: `${((day.temperature_max - day.temperature_min) / 40) * 100}%`
                    }}
                  />
                </div>
                <span className="temp-low">
                  {Math.round(day.temperature_min)}{DEGREE_SYMBOL}
                </span>
              </div>

              <div className="tv-forecast-wind">
                <Icon name="wind" size={18} />
                <span>{Math.round(day.wind_speed)} mph</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default TVForecast;